/**
 * Clip-state tracker for `ThreeJsManager` overlays that don't ride the
 * engine's automatic clipping pipeline.
 *
 * The Web3D engine clips native geometry models (point clouds, TrimBIM,
 * trajectories, alignment markers) automatically when a clip plane or clip
 * box is active. Custom Three.js overlays attached via `ThreeJsManager`
 * (station markers, station path segments, etc.) are rendered as
 * "always-on-top" decorations with `depthTest: false` and don't participate
 * in that clipping — they would otherwise float in mid-air after the user
 * clips into a building.
 *
 * Capabilities that own such overlays use `createClipStateTracker(viewer)`
 * to subscribe to viewer-level clip events and query cheap predicates
 * (`isPointInsideActiveClip`, `isSegmentVisibleInActiveClip`) from a
 * per-frame update loop. The tracker is pane-local (single viewer).
 *
 * Don't use this for native geometry models — they are clipped by the
 * engine automatically.
 *
 * Future work (additive): `clipLineSegment(start, end)` returning the
 * visible sub-segment when a line crosses the clip boundary. Today's
 * `isSegmentVisibleInActiveClip` uses a "hide-if-either-endpoint-clipped"
 * policy, acceptable for short station-to-station path segments.
 */
import {
  ClipBox,
  ClipBoxTool,
  ClipPlane,
  ClipPlaneTool,
  Matrix4,
  Vector3,
  Web3DViewer,
} from '@technology/web3d';
import { Accessor, createSignal, getOwner, onCleanup } from 'solid-js';

export interface ClipState {
  readonly planes: readonly ClipPlane[];
  readonly box: ClipBox | undefined;
}

export interface ClipStateTracker {
  /** Reactive accessor that fires on every clip event (planes and box). */
  readonly state: Accessor<ClipState>;
  /** True when `point` is inside every active clip plane and the active clip box. */
  isPointInsideActiveClip(point: Vector3): boolean;
  /** True when both endpoints are inside the active clip volume (hide-if-either-clipped). */
  isSegmentVisibleInActiveClip(start: Vector3, end: Vector3): boolean;
  /**
   * Manually remove the viewer event listeners. Only needed when the tracker
   * is created outside a reactive owner. Created inside a `createRoot` (or
   * any reactive scope), the tracker registers `onCleanup` itself and is
   * disposed automatically when the enclosing owner is disposed.
   */
  dispose(): void;
}

const CLIP_EVENTS = [
  'clipplaneadded',
  'clipplanemodified',
  'clipplaneremoved',
  'clipboxadded',
  'clipboxmodified',
  'clipboxremoved',
] as const;

export function createClipStateTracker(viewer: Web3DViewer): ClipStateTracker {
  const clipPlaneTool = viewer.tools.get(ClipPlaneTool);
  const clipBoxTool = viewer.tools.get(ClipBoxTool);

  // Tools may resolve to a stub that lacks the typed clip methods (e.g. test
  // mocks that return a generic enable/disable shell from `tools.get`). Guard
  // each accessor so the tracker can attach harmlessly in those contexts.
  const readPlanes = (): readonly ClipPlane[] =>
    typeof clipPlaneTool?.getClipPlanes === 'function'
      ? clipPlaneTool.getClipPlanes()
      : [];
  const readBox = (): ClipBox | undefined =>
    typeof clipBoxTool?.getClipBoxes === 'function'
      ? clipBoxTool.getClipBoxes()?.[0]
      : undefined;

  const readState = (): ClipState => ({
    planes: readPlanes(),
    box: readBox(),
  });

  // `equals: false` because the underlying ClipPlane / ClipBox objects may
  // be mutated in place (e.g. drag-to-modify) so reference equality can't
  // detect change.
  const [state, setState] = createSignal<ClipState>(readState(), {
    equals: false,
  });

  const refresh = (): void => {
    setState(readState());
  };
  const refreshListener = refresh as EventListener;

  for (const event of CLIP_EVENTS) {
    viewer.addEventListener(event, refreshListener);
  }

  // Reusable scratch objects for the hot path.
  const localPoint = new Vector3();
  const inverseMatrix = new Matrix4();

  const isPointInsideActiveClip = (point: Vector3): boolean => {
    const current = state();

    // three.js / Web3D convention: distanceToPoint < 0 means clipped.
    for (const plane of current.planes) {
      if (plane.distanceToPoint(point) < 0) {
        return false;
      }
    }

    // ClipBox is an oriented bounding box: transform the world point into
    // the box's local frame and test against the local AABB. The engine
    // updates matrixWorld during render, but we may be queried from a forced
    // refresh before the next frame.
    if (current.box) {
      current.box.updateMatrixWorld(true);
      inverseMatrix.copy(current.box.matrixWorld).invert();
      localPoint.copy(point).applyMatrix4(inverseMatrix);
      if (!current.box.getBox().containsPoint(localPoint)) {
        return false;
      }
    }

    return true;
  };

  const isSegmentVisibleInActiveClip = (
    start: Vector3,
    end: Vector3
  ): boolean => isPointInsideActiveClip(start) && isPointInsideActiveClip(end);

  let disposed = false;
  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    for (const event of CLIP_EVENTS) {
      viewer.removeEventListener(event, refreshListener);
    }
  };

  // Match the lifecycle of standard Solid factories (`createMemo`, `createEffect`):
  // when called inside a reactive owner the tracker auto-cleans on owner
  // disposal, so consumers don't need to track its lifetime manually. This is
  // what removes the listeners on view-mode change in
  // `view-mode-capability-coordinator`'s per-invocation `createRoot`.
  if (getOwner()) {
    onCleanup(dispose);
  }

  return {
    state,
    isPointInsideActiveClip,
    isSegmentVisibleInActiveClip,
    dispose,
  };
}
